import React, { useState, useEffect, useRef, useCallback } from "react";
import _ from "lodash";
import BarcodeReader from "react-barcode-reader";
import { connect, ConnectedProps } from "react-redux";
import { RootState } from "../store/store";
import {
  Col,
  Collapse,
  Drawer,
  Form,
  Input,
  InputNumber,
  Row,
  Slider,
  notification,
  Button,
  Image,
  Switch,
} from "antd";
import { API_BASE_URL } from "../config";
import { SettingFilled, CloseOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { useSpeechSynthesis } from "react-speech-kit";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { queryApi } from "../store/actions/common";
import { triggerNotification } from "../store/directActions";
import { useSocket } from "../hooks/useSocket";
import Snowfall from "react-snowfall";
import { userActions } from "../store/slices/user";

import useInterval from "../utils/useInterval";
import soundLucky from "../assets/lucky_ticket.mpeg";
import soundNew from "../assets/new_ticket.mpeg";
import soundEnding from "../assets/ending_ticket.mpeg";
import useAudio from "../utils/useAudio";
import SlotDisplay from "./SlotDisplayV3";
import SocketLogModal from "../components/SocketLogModal";
import BackgroundVideo from "../components/BackgroundVideo";
import ColorPicker from "../components/ColorPicker";
import SingleUpload from "../components/SingleUpload";
import { queryClient } from "../store/queryClient";

const { Panel } = Collapse;

const sounds = {
  2: soundLucky,
  1: soundNew,
  3: soundEnding,
  0: soundNew,
};

const soundsBarcode = {
  error:
    "https://soundbible.com/mp3/Computer%20Error%20Alert-SoundBible.com-783113881.mp3",
  success: "https://soundbible.com/mp3/Bleep-SoundBible.com-1927126940.mp3",
};

const backgrounds = [
  {
    filetype: "basic",
    color: "#fff",
  },
  {
    filetype: "basic",
    color: "#000",
  },
  {
    filetype: "gradiant",
    color: `linear-gradient(-45deg,#f79533,#f37055,#ef4e7b,#a166ab,#5073b8,#1098ad,#07b39b,#6fba82)`,
  },
  {
    filetype: "gradiant",
    color: `linear-gradient(129deg, #FFFFFF 0%, #6284FF 54%, #FF0000 100%)`,
  },
  {
    filetype: "gradiant",
    color: `linear-gradient( 68.1deg,  rgba(196,69,69,1) 9.2%, rgba(255,167,73,0.82) 25%, rgba(253,217,82,0.82) 43.4%, rgba(107,225,108,0.82) 58.2%, rgba(107,169,225,0.82) 75.1%, rgba(153,41,243,0.82) 87.3% )`,
  },
  {
    filetype: "image/gif",
    img_id: "25818c9f-8c86-49a7-a700-29e496992fe3",
    name: "15865.gif",
    path: "original/25818c9f-8c86-49a7-a700-29e496992fe3.gif",
    status: true,
    url: "https://lottery-data-v2.s3.us-east-2.amazonaws.com/original/25818c9f-8c86-49a7-a700-29e496992fe3.gif",
  },
];

interface OwnProps {}

type PageScreenV3Props = OwnProps & PropsFromRedux;

// A stable per-device id so the store can tell multiple TVs of the same
// store apart. Persisted so it survives reloads/reconnects.
const SCREEN_ID_KEY = "live-screen-id";
const getScreenId = (): string => {
  let id = localStorage.getItem(SCREEN_ID_KEY);
  if (!id) {
    id = `screen-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem(SCREEN_ID_KEY, id);
  }
  return id;
};

const SCREEN_HEARTBEAT_MS = 8000;

function PageScreenV3({
  is_staff = false,
  userId,
  logout,
  isAuth,
}: PageScreenV3Props) {
  const navigate = useNavigate();
  const queryClientHook = useQueryClient();
  const themeKey = "themeConfigv1";

  const [totalItemCount, setTotalItemCount] = useState(0);
  const [totalLargeCount, setTotalLargeCount] = useState(0);
  const [showMeta, toggleMeta] = useState(false);
  const [showAmontTicket, setAmountTicket] = useState("0");
  const [showScannedBox, updateScannedBox] = useState(0);
  const [megaTitle, setMegaTitle] = useState("Mega Million");
  const [powerTitle, setPowerTitle] = useState("Powerwall");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showTypeTicket, setTypeTicket] = useState("0");
  const [playSound, toggleSound] = React.useState(false);
  const [playSoundType, setSoundType] = React.useState(0);
  const [randomSlots, setRandomSlots] = React.useState<number[]>([]);
  const [announcementCycle, setAnnouncementCycle] = useState(0);
  const [showSocketLog, setShowSocketLog] = useState(false);
  const [themeValues, setThemeValues] = useState<any>();

  const [playing, toggle] = useAudio(sounds[1]);
  const [playingBarcodeSound, toggleBarcodeSound] = useAudio(soundsBarcode[1]);
  const screenRef = useRef<HTMLDivElement>(null);
  const [form] = Form.useForm();

  const { speak, cancel, speaking } = useSpeechSynthesis({
    onEnd: () => {
      console.log("speech ended");
    },
  });

  // Fetch slot data
  const { data: slotData = [] } = useQuery({
    queryKey: ["slot", { is_screen: true }],
    queryFn: () => queryApi(`slot?is_screen=true`, "GET"),
    staleTime: Infinity,
    gcTime: 0,
  });

  // Fetch store meta
  const { data: storeMeta = [] } = useQuery({
    queryKey: ["store", "user"],
    queryFn: () => queryApi(`store/user`, "GET"),
    staleTime: Infinity,
    gcTime: 0,
  });

  // Fetch store config
  const { data: storeConfig } = useQuery({
    queryKey: ["store", "config"],
    queryFn: () => queryApi(`store/config`, "GET"),
    staleTime: Infinity,
    gcTime: 0,
  });

  // Fetch theme config
  const { data: themeConfig } = useQuery({
    queryKey: ["theme", "config"],
    queryFn: () => queryApi(`store/theme`, "GET"),
    staleTime: Infinity,
    gcTime: 0,
  });

  // Fetch CMS data
  const { data: cmsData = [] } = useQuery({
    queryKey: ["cms", "dim.screen.backgrounds"],
    queryFn: () =>
      queryApi(`manage/cms?entity_id=dim.screen.backgrounds`, "GET"),
    staleTime: Infinity,
    gcTime: 0,
  });

  // Fetch personal CMS data
  const { data: personalCms = [] } = useQuery({
    queryKey: ["cms", `dim.screen.backgrounds.${userId}`],
    queryFn: () =>
      queryApi(`manage/cms?entity_id=dim.screen.backgrounds.${userId}`, "GET"),
    enabled: !!userId,
    staleTime: Infinity,
    gcTime: 0,
  });

  // Mutation for saving theme
  const { mutate: saveThemeConfig } = useMutation({
    mutationFn: (data: any) => queryApi(`store/theme`, "POST", data),
    onSuccess: () => {
      queryClientHook.invalidateQueries({
        queryKey: ["theme", "config"],
      });
      triggerNotification({
        title: "Success",
        description: "Theme saved successfully",
        type: "success",
      });
    },
    onError: (error: any) => {
      triggerNotification({
        title: "Error",
        description: error.message || "Failed to save theme",
        type: "error",
      });
    },
  });

  // Mutation for adding CMS
  const { mutate: addCMS } = useMutation({
    mutationFn: ({ data, entity }: { data: string; entity: string }) =>
      queryApi(`manage/cms`, "POST", { data, entity_id: entity }),
    onSuccess: () => {
      queryClientHook.invalidateQueries({
        queryKey: ["cms"],
      });
    },
  });

  // Mutation for removing CMS
  const { mutate: removeCMS } = useMutation({
    mutationFn: (cmsId: string) => queryApi(`manage/cms/${cmsId}`, "DELETE"),
    onSuccess: () => {
      queryClientHook.invalidateQueries({
        queryKey: ["cms"],
      });
    },
  });

  const userCms = personalCms || [];

  const defaultTheme = {
    showSnowfall: false,
    borderColor: {
      1: "#8ee0ff",
      2: "#FF3D68",
      3: "#8099ff",
      5: "#07DA0F",
      7: "#753422",
      10: "#FF6600",
      20: "#FF3F00",
      25: "#A03C78",
      30: "#71EFA3",
      50: "#DA0037",
    },
    boxColor: {
      1: "#8ee0ff",
      2: "#FF3D68",
      3: "#8099ff",
      5: "#07DA0F",
      7: "#753422",
      10: "#FF6600",
      20: "#FF3F00",
      25: "#A03C78",
      30: "#71EFA3",
      50: "#DA0037",
    },
    fontColor: {
      1: "#ffffff",
      2: "#ffffff",
      3: "#ffffff",
      5: "#ffffff",
      7: "#ffffff",
      10: "#ffffff",
      20: "#ffffff",
      25: "#ffffff",
      30: "#ffffff",
      50: "#ffffff",
    },
    boxBorderWidth: {
      1: 1,
      2: 1,
      3: 1,
      5: 1,
      7: 1,
      10: 1,
      20: 1,
      25: 1,
      30: 1,
      50: 1,
    },
    borderRadius: 7,
    headerTitleColor: "#ffffff",
    fontSize: undefined,
    backgroundImage: undefined,
    cardRatio: 1.2,
    boxTextColor: {
      1: "#ffffff",
      2: "#ffffff",
      3: "#ffffff",
      5: "#ffffff",
      7: "#ffffff",
      10: "#ffffff",
      20: "#ffffff",
      25: "#ffffff",
      30: "#ffffff",
      50: "#ffffff",
    },
    bottomBoxBorderRadius: 7,
    translateY: {
      1: 0,
      2: 0,
      3: 0,
      5: 0,
      7: 0,
      10: 0,
      20: 0,
      25: 0,
      30: 0,
      50: 0,
    },
    tagBox: {
      boxColor: {
        1: "#ff0000",
        2: "#ff0000",
        3: "#ff0000",
        5: "#ff0000",
        7: "#ff0000",
        10: "#ff0000",
        20: "#ff0000",
        25: "#ff0000",
        30: "#ff0000",
        50: "#ff0000",
      },
      fontColor: {
        1: "#ffffff",
        2: "#ffffff",
        3: "#ffffff",
        5: "#ffffff",
        7: "#ffffff",
        10: "#ffffff",
        20: "#ffffff",
        25: "#ffffff",
        30: "#ffffff",
        50: "#ffffff",
      },
    },
    boxPosition: {
      horizontalPosition: {
        1: 0,
        2: 0,
        3: 0,
        5: 0,
        7: 0,
        10: 0,
        20: 0,
        25: 0,
        30: 0,
        50: 0,
      },
      verticalPosition: {
        1: 0,
        2: 0,
        3: 0,
        5: 0,
        7: 0,
        10: 0,
        20: 0,
        25: 0,
        30: 0,
        50: 0,
      },
    },
    centerBoxFontSize: {
      1: undefined,
      2: undefined,
      3: undefined,
      5: undefined,
      7: undefined,
      10: undefined,
      20: undefined,
      25: undefined,
      30: undefined,
      50: undefined,
    },
    bottomBoxFontSize: {
      1: undefined,
      2: undefined,
      3: undefined,
      5: undefined,
      7: undefined,
      10: undefined,
      20: undefined,
      25: undefined,
      30: undefined,
      50: undefined,
    },
  };

  const configTheme = useCallback(async () => {
    if (!themeConfig || !themeConfig.value) {
      localStorage.setItem(themeKey, JSON.stringify(defaultTheme));
      setThemeValues(defaultTheme);
      form.setFieldsValue(defaultTheme);
    } else {
      const themeValuesParsed = JSON.parse(themeConfig.value);
      localStorage.setItem(
        themeKey,
        JSON.stringify({ ...defaultTheme, ...themeValuesParsed }),
      );
      setThemeValues({ ...defaultTheme, ...themeValuesParsed });
      form.setFieldsValue({ ...defaultTheme, ...themeValuesParsed });
    }
  }, [themeConfig, form]);

  useEffect(() => {
    configTheme();
  }, [configTheme]);

  // Refresh the on-screen slot data. Called on the "screen-refresh" event
  // and again whenever the socket reconnects after a drop, so a live screen
  // that missed events while offline recovers its state.
  const refreshScreen = useCallback(() => {
    queryClientHook.invalidateQueries({
      queryKey: ["slot"],
    });
  }, [queryClientHook]);

  // Handle store update messages
  const handleStoreUpdate = useCallback(
    (data: any) => {
      if (data?.topic === "client-refresh") {
        queryClientHook.invalidateQueries({
          queryKey: ["store", "user"],
        });
      }
      if (data?.topic === "logout") {
        logout();
        navigate("/login");
      }
      if (data?.topic === "ticket-refresh") {
        let message = JSON.parse(data.message);
        updateScannedBox(message["slot_number"]);
      }
      if (data?.topic === "event_screen_ticket_type") {
        setTypeTicket(data.message);
        toggleSound(true);
        toggle({
          status: true,
          url: sounds[parseInt(data.message)],
          isLoop: true,
          sequence: moment().valueOf(),
        });
      }
      if (data?.topic === "event_screen_amount") {
        setAmountTicket(data.message);
      }
      if (data?.topic === "screen-refresh") {
        refreshScreen();
      }
      if (data?.topic === "screen-reload") {
        window.location.reload();
      }
      if (data?.topic === "theme-refresh") {
        queryClient.invalidateQueries({ queryKey: ["theme", "config"] });
      }
      if (data?.topic === "theme-semi-refresh") {
        setThemeValues(data.message);
      }
      if (data?.topic === "theme-semi-reset") {
        const lastConfig = localStorage.getItem(themeKey);
        if (lastConfig) {
          setThemeValues(JSON.parse(lastConfig));
        } else {
          setThemeValues(defaultTheme);
        }
      }
    },
    [logout, navigate, queryClientHook, configTheme, refreshScreen],
  );

  // Subscribe to store-update topic
  const { sendMessage, isConnected } = useSocket({
    rooms: userId,
    subscriptions: {
      "store-update": handleStoreUpdate,
    },
    onReconnect: refreshScreen,
  });

  // ---- Live-screen presence (only the real TV, not the staff preview) ------
  const screenId = useRef<string>(getScreenId());

  const announceScreen = useCallback(() => {
    if (is_staff) return;
    sendMessage("screen-connected", {
      id: screenId.current,
      name: storeMeta?.[0]?.name || navigator.platform || "Live Screen",
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      resolution: `${window.screen?.width || 0}x${window.screen?.height || 0}`,
      version: process.env.REACT_APP_VERSION,
      path: window.location.href,
      connectedAt: Date.now(),
    });
  }, [is_staff, sendMessage, storeMeta]);

  // Announce on (re)connect and heartbeat so the store can detect drops.
  useEffect(() => {
    if (is_staff) return;
    if (isConnected) announceScreen();
    const heartbeat = setInterval(() => {
      if (isConnected) announceScreen();
    }, SCREEN_HEARTBEAT_MS);
    return () => clearInterval(heartbeat);
  }, [is_staff, isConnected, announceScreen]);

  // Best-effort goodbye when the screen navigates away or the tab closes.
  // Abrupt drops (power/network loss) are handled by the store-side timeout.
  useEffect(() => {
    if (is_staff) return;
    const bye = () => {
      sendMessage("screen-disconnected", { id: screenId.current });
    };
    window.addEventListener("pagehide", bye);
    window.addEventListener("beforeunload", bye);
    return () => {
      bye();
      window.removeEventListener("pagehide", bye);
      window.removeEventListener("beforeunload", bye);
    };
  }, [is_staff, sendMessage]);

  useEffect(() => {
    if (slotData && Array.isArray(slotData) && slotData.length > 0) {
      setTotalItemCount(slotData.length);
      setTotalLargeCount(
        slotData.filter((item: any) => item.size === "large").length,
      );
    }
  }, [slotData]);

  useEffect(() => {
    if (
      storeMeta &&
      Array.isArray(storeMeta) &&
      storeMeta[0] &&
      !storeMeta[0].show_display
    ) {
      navigate("/admin/dashboard");
    }
  }, [storeMeta, navigate]);

  useEffect(() => {
    document.body.style.zoom = 1.0;
  }, []);

  // The live display has its own background (BackgroundVideo below) and must
  // not inherit the store's app theme. This has to be set on <body>, not on a
  // wrapper element: BackgroundVideo is position:fixed with z-index:-1, and a
  // negative-z-index layer paints *below* in-flow content, so an opaque
  // wrapper background covers it completely — visible only in fullscreen,
  // where the fullscreen element gets its own stacking context. A background
  // on <body> propagates to the canvas instead, which sits below everything.
  useEffect(() => {
    document.body.classList.add("live-display");
    return () => document.body.classList.remove("live-display");
  }, []);

  useInterval(() => {
    toggleMeta(!showMeta);
  }, 10000);

  useInterval(() => {
    if (is_staff) return;
    updateScannedBox(0);
  }, 1000);

  useEffect(() => {
    generateRandomNumbers();
    let timer1: NodeJS.Timeout;
    if (showTypeTicket !== "0" || showAmontTicket !== "0") {
      timer1 = setTimeout(() => {
        setAmountTicket("0");
        setTypeTicket("0");
      }, 9 * 1000);
    }
    return () => {
      if (timer1) clearTimeout(timer1);
    };
  }, [showAmontTicket, showTypeTicket]);

  const getGameInfo = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}api/proxy`, {
        method: "GET",
      });
      if (response.status === 200) {
        const data = await response.json();
        setMegaTitle(`$${data.mega}`);
        setPowerTitle(`$${data.powerball}`);
      } else {
        setMegaTitle("$0 MILLION");
        setPowerTitle("$0 MILLION");
      }
    } catch (error) {
      setMegaTitle("$0 MILLION");
      setPowerTitle("$0 MILLION");
    }
  };

  useEffect(() => {
    getGameInfo();
  }, []);

  const generateRandomNumbers = () => {
    if (totalItemCount <= 5) return;
    const arr: number[] = [];
    while (arr.length < 5) {
      const r = Math.floor(Math.random() * totalItemCount);
      if (arr.indexOf(r) === -1) arr.push(r);
    }
    setRandomSlots(arr);
  };

  const playDisplaySound = (text: string) => {
    console.log("INFO => play sound started", text, speaking);
    if (speaking) cancel();
    if (text) speak({ text: text });
    console.log("INFO => play sound completed", text);
  };

  const playAnnouncements = () => {
    console.log("INFO => play announcements");
    let soundType = playSoundType;
    if (soundType + 1 > 3) {
      setSoundType(1);
    } else {
      setSoundType(soundType + 1);
    }
    if (soundType > 0) {
      setTypeTicket("" + soundType);
      toggle({
        status: true,
        url: sounds[soundType],
        isLoop: true,
        sequence: moment().valueOf(),
      });
      toggleSound(true);
    }
  };

  useInterval(
    () => {
      console.log("INFO => jackpot sound enabled", is_staff);
      if (is_staff) return;
      getGameInfo();
      if (announcementCycle % 3 === 0 && announcementCycle !== 0) {
        playAnnouncements();
        setAnnouncementCycle(0);
      } else {
        if (megaTitle !== "Mega Million" && powerTitle !== "Powerwall") {
          playDisplaySound(`Megamillion jackpot is estimated ${megaTitle}`);
          setTimeout(() => {
            playDisplaySound(
              `and Powerball jackpot is estimated ${powerTitle}, try you luck today!`,
            );
          }, 1000 * 5);
        } else {
          console.log("INFO => Unable to play jackpot sound");
        }
        setAnnouncementCycle(announcementCycle + 1);
      }
    },
    1000 * 60 * 10,
  );

  const handleScan = (code: string, reason: string) => {
    console.log("sending scan event", reason, code);
    sendMessage("client-scan", {
      scan_code: code,
    });
  };

  const handleFullScreen = () => {
    const screen = screenRef.current;
    if (screen && screen.requestFullscreen) {
      screen.requestFullscreen();
    }
  };

  function arrayToObject(array: number[], value: any) {
    return array.reduce((obj: any, item) => {
      obj[item] = value;
      return obj;
    }, {});
  }

  document.title = is_staff ? "View Display" : "Live Display";

  return (
    <div style={{ height: "100dvh", width: "100%", position: "relative" }}>
      {is_staff && (
        <div
          className="setting-button"
          style={{
            borderRadius: 0,
            position: "absolute",
            top: 20,
            right: 0,
            padding: 18,
            backgroundColor: "#fff",
            aspectRatio: 1,
            zIndex: 100,
          }}
          onClick={() => {
            setDrawerOpen(true);
            form.setFieldsValue(themeValues);
          }}
        >
          <SettingFilled style={{ color: "#000", fontSize: 40 }} />
        </div>
      )}

      <div
        style={{
          position: "absolute",
          overflow: "hidden",
          height: "60%",
          width: "30%",
          bottom: 0,
          right: 0,
        }}
      >
        <Drawer
          title="Display settings"
          placement="bottom"
          getContainer={false}
          height={"100%"}
          style={{
            position: "absolute",
          }}
          onClose={() => {
            setDrawerOpen(false);
            const lastConfig = localStorage.getItem(themeKey);
            if (lastConfig) {
              setThemeValues(JSON.parse(lastConfig));
            } else {
              setThemeValues(defaultTheme);
            }
          }}
          bodyStyle={{ padding: 0 }}
          open={drawerOpen}
          footer={
            <div style={{ textAlign: "right" }}>
              <Button
                onClick={async () => {
                  setDrawerOpen(false);
                  localStorage.setItem(themeKey, JSON.stringify(defaultTheme));
                  setThemeValues(defaultTheme);
                  await saveThemeConfig(defaultTheme);
                  sendMessage("theme-refresh");
                }}
                style={{ marginRight: 8 }}
              >
                Reset
              </Button>
              <Button
                onClick={() => {
                  setDrawerOpen(false);
                  const lastConfig = localStorage.getItem(themeKey);
                  if (lastConfig) {
                    setThemeValues(JSON.parse(lastConfig));
                  } else {
                    setThemeValues(defaultTheme);
                  }
                }}
                style={{ marginRight: 8 }}
              >
                Cancel
              </Button>
              <Button type="primary" onClick={form.submit}>
                Submit
              </Button>
            </div>
          }
        >
          <Form
            onValuesChange={(_, allValues) => {
              setThemeValues(allValues);
            }}
            preserve
            onFinish={async (e) => {
              setDrawerOpen(false);
              localStorage.setItem(themeKey, JSON.stringify(e));
              await saveThemeConfig(e);
              sendMessage("theme-refresh");
            }}
            layout="vertical"
            form={form}
          >
            <Collapse defaultActiveKey={["1"]} accordion>
              <Panel forceRender header="Card Meta" key="1">
                <Form.Item
                  label="Show Snowfall"
                  name="showSnowfall"
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
                <Form.Item label={"Border Radius"} name="borderRadius">
                  <Slider max={50} min={0} />
                </Form.Item>
                <Form.Item
                  label={"Bottom Box Border Radius"}
                  name="bottomBoxBorderRadius"
                >
                  <Slider max={50} min={0} />
                </Form.Item>
                <Form.Item label={"Card Ratio"} name="cardRatio">
                  <Slider max={5} min={1} step={0.1} />
                </Form.Item>
                <Form.Item label={"Font Size"} name="fontSize">
                  <InputNumber />
                </Form.Item>
                <Form.Item name={"headerTitleColor"} label="Header Title Color">
                  <ColorPicker />
                </Form.Item>
                <p style={{ margin: 0, color: "#000" }}>Border</p>
                <Row gutter={[16, 16]}>
                  <Col span={8}>
                    <div style={{ color: "#000", textAlign: "center" }}>
                      Ticket
                    </div>
                  </Col>
                  <Col span={8}>
                    <div style={{ color: "#000", textAlign: "center" }}>
                      Border Color
                    </div>
                  </Col>
                  <Col span={8}>
                    <div style={{ color: "#000", textAlign: "center" }}>
                      Border Width
                    </div>
                  </Col>
                  <Col span={8}>
                    <div style={{ color: "#000", textAlign: "center" }}>
                      All
                    </div>
                  </Col>
                  <Col span={8} style={{ color: "#000", textAlign: "center" }}>
                    <ColorPicker
                      onChange={(e) => {
                        form.setFieldsValue({
                          borderColor: arrayToObject(
                            [1, 2, 3, 5, 7, 10, 20, 25, 30, 50],
                            e,
                          ),
                        });
                        setThemeValues(form.getFieldsValue());
                      }}
                    />
                  </Col>
                  <Col span={8}>
                    <InputNumber
                      style={{ width: "100%" }}
                      onChange={(e) => {
                        form.setFieldsValue({
                          boxBorderWidth: arrayToObject(
                            [1, 2, 3, 5, 7, 10, 20, 25, 30, 50],
                            e,
                          ),
                        });
                        setThemeValues(form.getFieldsValue());
                      }}
                    />
                  </Col>
                  {[1, 2, 3, 5, 7, 10, 20, 25, 30, 50].map((item) => (
                    <React.Fragment key={item}>
                      <Col span={8}>
                        <div style={{ color: "#000", textAlign: "center" }}>
                          {item}
                        </div>
                      </Col>
                      <Col span={8}>
                        <Form.Item
                          style={{ marginBottom: 0, textAlign: "center" }}
                          name={["borderColor", `${item}`]}
                          preserve
                        >
                          <ColorPicker />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item
                          name={["boxBorderWidth", `${item}`]}
                          style={{ marginBottom: 0, textAlign: "center" }}
                        >
                          <InputNumber style={{ width: "100%" }} />
                        </Form.Item>
                      </Col>
                    </React.Fragment>
                  ))}
                </Row>
              </Panel>

              <Panel forceRender header="Center Box" key="2">
                <Row gutter={[16, 16]}>
                  <Col span={4}>
                    <div style={{ color: "#000", textAlign: "center" }}>
                      Ticket
                    </div>
                  </Col>
                  <Col span={4}>
                    <div style={{ color: "#000", textAlign: "center" }}>
                      Box Color
                    </div>
                  </Col>
                  <Col span={4}>
                    <div style={{ color: "#000", textAlign: "center" }}>
                      Font Color
                    </div>
                  </Col>
                  <Col span={4}>
                    <div style={{ color: "#000", textAlign: "center" }}>
                      Horizontal position
                    </div>
                  </Col>
                  <Col span={4}>
                    <div style={{ color: "#000", textAlign: "center" }}>
                      Vertical position
                    </div>
                  </Col>
                  <Col span={4}>
                    <div style={{ color: "#000", textAlign: "center" }}>
                      Font Size
                    </div>
                  </Col>
                  <Col span={4}>
                    <div style={{ color: "#000", textAlign: "center" }}>
                      All
                    </div>
                  </Col>
                  <Col span={4} style={{ color: "#000", textAlign: "center" }}>
                    <ColorPicker
                      onChange={(e) => {
                        form.setFieldsValue({
                          centerBoxColor: arrayToObject(
                            [1, 2, 3, 5, 7, 10, 20, 25, 30, 50],
                            e,
                          ),
                        });
                        setThemeValues(form.getFieldsValue());
                      }}
                    />
                  </Col>
                  <Col span={4} style={{ color: "#000", textAlign: "center" }}>
                    <ColorPicker
                      onChange={(e) => {
                        form.setFieldsValue({
                          boxTextColor: arrayToObject(
                            [1, 2, 3, 5, 7, 10, 20, 25, 30, 50],
                            e,
                          ),
                        });
                        setThemeValues(form.getFieldsValue());
                      }}
                    />
                  </Col>
                  <Col span={4} style={{ color: "#000", textAlign: "center" }}>
                    <InputNumber
                      style={{ width: "100%" }}
                      onChange={(e) => {
                        form.setFieldsValue({
                          boxPosition: {
                            horizontalPosition: arrayToObject(
                              [1, 2, 3, 5, 7, 10, 20, 25, 30, 50],
                              e,
                            ),
                          },
                        });
                        setThemeValues(form.getFieldsValue());
                      }}
                    />
                  </Col>
                  <Col span={4} style={{ color: "#000", textAlign: "center" }}>
                    <InputNumber
                      style={{ width: "100%" }}
                      onChange={(e) => {
                        form.setFieldsValue({
                          boxPosition: {
                            verticalPosition: arrayToObject(
                              [1, 2, 3, 5, 7, 10, 20, 25, 30, 50],
                              e,
                            ),
                          },
                        });
                        setThemeValues(form.getFieldsValue());
                      }}
                    />
                  </Col>
                  <Col span={4}>
                    <InputNumber
                      style={{ width: "100%" }}
                      onChange={(e) => {
                        form.setFieldsValue({
                          centerBoxFontSize: arrayToObject(
                            [1, 2, 3, 5, 7, 10, 20, 25, 30, 50],
                            e,
                          ),
                        });
                        setThemeValues(form.getFieldsValue());
                      }}
                    />
                  </Col>
                  {[1, 2, 3, 5, 7, 10, 20, 25, 30, 50].map((item) => (
                    <React.Fragment key={item}>
                      <Col span={4}>
                        <div style={{ color: "#000", textAlign: "center" }}>
                          {item}
                        </div>
                      </Col>
                      <Col span={4}>
                        <Form.Item
                          name={["centerBoxColor", `${item}`]}
                          style={{ marginBottom: 0, textAlign: "center" }}
                        >
                          <ColorPicker />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item
                          name={["boxTextColor", `${item}`]}
                          style={{ marginBottom: 0, textAlign: "center" }}
                        >
                          <ColorPicker />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item
                          style={{ marginBottom: 0, textAlign: "center" }}
                          name={[
                            "boxPosition",
                            "horizontalPosition",
                            `${item}`,
                          ]}
                        >
                          <InputNumber style={{ width: "100%" }} />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item
                          style={{ marginBottom: 0, textAlign: "center" }}
                          name={["boxPosition", "verticalPosition", `${item}`]}
                        >
                          <InputNumber style={{ width: "100%" }} />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item
                          style={{ marginBottom: 0, textAlign: "center" }}
                          name={["centerBoxFontSize", `${item}`]}
                        >
                          <InputNumber style={{ width: "100%" }} />
                        </Form.Item>
                      </Col>
                    </React.Fragment>
                  ))}
                </Row>
              </Panel>

              <Panel forceRender header="Bottom Box" key="3">
                <Row gutter={[16, 16]}>
                  <Col span={4}>
                    <div style={{ color: "#000", textAlign: "center" }}>
                      Ticket
                    </div>
                  </Col>
                  <Col span={4}>
                    <div style={{ color: "#000", textAlign: "center" }}>
                      Box Color
                    </div>
                  </Col>
                  <Col span={4}>
                    <div style={{ color: "#000", textAlign: "center" }}>
                      Font Color
                    </div>
                  </Col>
                  <Col span={4}>
                    <div style={{ color: "#000", textAlign: "center" }}>
                      Font Size
                    </div>
                  </Col>
                  <Col span={4}>
                    <div style={{ color: "#000", textAlign: "center" }}>
                      Box position
                    </div>
                  </Col>
                  <Col span={4}></Col>
                  <Col span={4}>
                    <div style={{ color: "#000", textAlign: "center" }}>
                      All
                    </div>
                  </Col>
                  <Col span={4} style={{ color: "#000", textAlign: "center" }}>
                    <ColorPicker
                      onChange={(e) => {
                        form.setFieldsValue({
                          boxColor: arrayToObject(
                            [1, 2, 3, 5, 7, 10, 20, 25, 30, 50],
                            e,
                          ),
                        });
                        setThemeValues(form.getFieldsValue());
                      }}
                    />
                  </Col>
                  <Col span={4} style={{ color: "#000", textAlign: "center" }}>
                    <ColorPicker
                      onChange={(e) => {
                        form.setFieldsValue({
                          fontColor: arrayToObject(
                            [1, 2, 3, 5, 7, 10, 20, 25, 30, 50],
                            e,
                          ),
                        });
                        setThemeValues(form.getFieldsValue());
                      }}
                    />
                  </Col>
                  <Col span={4}>
                    <InputNumber
                      style={{ width: "100%" }}
                      onChange={(e) => {
                        form.setFieldsValue({
                          bottomBoxFontSize: arrayToObject(
                            [1, 2, 3, 5, 7, 10, 20, 25, 30, 50],
                            e,
                          ),
                        });
                        setThemeValues(form.getFieldsValue());
                      }}
                    />
                  </Col>
                  <Col span={4}>
                    <InputNumber
                      style={{ width: "100%" }}
                      onChange={(e) => {
                        form.setFieldsValue({
                          translateY: arrayToObject(
                            [1, 2, 3, 5, 7, 10, 20, 25, 30, 50],
                            e,
                          ),
                        });
                        setThemeValues(form.getFieldsValue());
                      }}
                    />
                  </Col>
                  <Col span={4}></Col>
                  {[1, 2, 3, 5, 7, 10, 20, 25, 30, 50].map((item) => (
                    <React.Fragment key={item}>
                      <Col span={4}>
                        <div style={{ color: "#000", textAlign: "center" }}>
                          {item}
                        </div>
                      </Col>
                      <Col span={4}>
                        <Form.Item
                          style={{ marginBottom: 0, textAlign: "center" }}
                          name={["boxColor", `${item}`]}
                          preserve
                        >
                          <ColorPicker />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item
                          style={{ marginBottom: 0, textAlign: "center" }}
                          name={["fontColor", `${item}`]}
                          preserve
                        >
                          <ColorPicker />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item
                          name={["bottomBoxFontSize", `${item}`]}
                          style={{ marginBottom: 0, textAlign: "center" }}
                        >
                          <InputNumber style={{ width: "100%" }} />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item
                          name={["translateY", `${item}`]}
                          style={{ marginBottom: 0, textAlign: "center" }}
                        >
                          <InputNumber style={{ width: "100%" }} />
                        </Form.Item>
                      </Col>
                      <Col span={4}></Col>
                    </React.Fragment>
                  ))}
                </Row>
              </Panel>

              <Panel forceRender header="New Tag" key="4">
                <Row gutter={[16, 16]}>
                  <Col span={8}>
                    <div style={{ color: "#000", textAlign: "center" }}>
                      Ticket
                    </div>
                  </Col>
                  <Col span={8}>
                    <div style={{ color: "#000", textAlign: "center" }}>
                      Background Color
                    </div>
                  </Col>
                  <Col span={8}>
                    <div style={{ color: "#000", textAlign: "center" }}>
                      Font Color
                    </div>
                  </Col>
                  <Col span={8}>
                    <div style={{ color: "#000", textAlign: "center" }}>
                      All
                    </div>
                  </Col>
                  <Col span={8} style={{ color: "#000", textAlign: "center" }}>
                    <ColorPicker
                      onChange={(e) => {
                        form.setFieldsValue({
                          tagBox: {
                            boxColor: arrayToObject(
                              [1, 2, 3, 5, 7, 10, 20, 25, 30, 50],
                              e,
                            ),
                          },
                        });
                        setThemeValues(form.getFieldsValue());
                      }}
                    />
                  </Col>
                  <Col span={8} style={{ color: "#000", textAlign: "center" }}>
                    <ColorPicker
                      onChange={(e) => {
                        form.setFieldsValue({
                          tagBox: {
                            fontColor: arrayToObject(
                              [1, 2, 3, 5, 7, 10, 20, 25, 30, 50],
                              e,
                            ),
                          },
                        });
                        setThemeValues(form.getFieldsValue());
                      }}
                    />
                  </Col>
                  {[1, 2, 3, 5, 7, 10, 20, 25, 30, 50].map((item) => (
                    <React.Fragment key={item}>
                      <Col span={8}>
                        <div style={{ color: "#000", textAlign: "center" }}>
                          {item}
                        </div>
                      </Col>
                      <Col span={8}>
                        <Form.Item
                          name={["tagBox", "boxColor", `${item}`]}
                          style={{ marginBottom: 0, textAlign: "center" }}
                        >
                          <ColorPicker />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item
                          name={["tagBox", "fontColor", `${item}`]}
                          style={{ marginBottom: 0, textAlign: "center" }}
                        >
                          <ColorPicker />
                        </Form.Item>
                      </Col>
                    </React.Fragment>
                  ))}
                </Row>
              </Panel>

              <Panel forceRender header="Background" key="5">
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    gap: 8,
                    flexWrap: "wrap",
                    marginBottom: 16,
                  }}
                >
                  {[
                    ...backgrounds,
                    ...(cmsData || []).map((i: any) => JSON.parse(i.data)),
                    ...(userCms || []).reverse().map((i: any) => ({
                      ...JSON.parse(i.data),
                      showDelete: true,
                      cms_id: i.cms_id,
                    })),
                  ].map((bg: any, index: number) => (
                    <div
                      key={index}
                      style={{
                        position: "relative",
                        height: 200,
                        width: 200,
                        border: "1px solid #a8a8a8",
                        background: bg?.filetype?.startsWith("image")
                          ? `url(${bg.url})`
                          : bg?.color,
                        backgroundSize: "cover",
                      }}
                      onClick={() => {
                        form.setFieldsValue({
                          backgroundImage: {
                            filetype: bg?.filetype,
                            color: bg?.color,
                            url: bg?.url,
                          },
                        });
                        setThemeValues(form.getFieldsValue());
                      }}
                    >
                      {bg.showDelete && (
                        <div
                          style={{
                            borderRadius: "50%",
                            backgroundColor: "black",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            position: "absolute",
                            padding: 4,
                            right: -12,
                            top: -6,
                            zIndex: 400,
                            cursor: "pointer",
                          }}
                          onClick={async (e) => {
                            e.stopPropagation();
                            await removeCMS(bg.cms_id);
                            queryClientHook.invalidateQueries({
                              queryKey: ["cms"],
                            });
                          }}
                        >
                          <CloseOutlined style={{ color: "white" }} />
                        </div>
                      )}
                      {bg.filetype.startsWith("video") && (
                        <video height="200" width="200" controls key={bg?.url}>
                          <source src={bg?.url} />
                          Your browser does not support the video tag.
                        </video>
                      )}
                    </div>
                  ))}
                  <Form.Item name="backgroundImage">
                    <SingleUpload
                      typeCheck={false}
                      hideResult={true}
                      returnResponse={true}
                      onChange={async (e: any) => {
                        await addCMS({
                          data: JSON.stringify(e),
                          entity: `dim.screen.backgrounds.${userId}`,
                        });
                        queryClientHook.invalidateQueries({
                          queryKey: ["cms"],
                        });
                      }}
                    />
                  </Form.Item>
                </div>
              </Panel>
            </Collapse>
          </Form>
        </Drawer>
      </div>
      <div
        ref={screenRef}
        id="screenObj"
        onClick={handleFullScreen}
        style={{
          height: "100%",
          width: "100%",
          justifyContent: "space-between",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          overflow: "hidden",
        }}
        className={!themeValues?.backgroundImage ? "screen" : ""}
      >
        {!!themeValues?.showSnowfall && (
          <Snowfall
            color="#82C3D9"
            snowflakeCount={800}
            style={{ zIndex: 100 }}
          />
        )}
        {!!themeValues?.backgroundImage && (
          <BackgroundVideo background={themeValues?.backgroundImage} />
        )}
        {/* @ts-ignore */}
        {!!storeMeta?.[0]?.show_header && (
          <div
            style={{
              height: "fit-content",
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            {_.isEmpty(storeMeta?.[0]?.store_meta_1) ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  textAlign: "center",
                  margin: "4px auto",
                }}
                className="blink-slow"
              >
                <div>
                  <Image
                    preview={false}
                    src={`https://lottery-data-v2.s3.us-east-2.amazonaws.com/original/fe580af0-c5f2-4ee5-9ac7-fbf013973205.png`}
                    style={{
                      maxHeight: 50,
                      maxWidth: 180,
                    }}
                  />
                </div>
                <div
                  style={{
                    fontWeight: 800,
                    fontSize: "0.5vw",
                    background: "yellow",
                    padding: "0.5vw",
                    color: "blue",
                    borderRadius: "10px",
                    margin: "auto 10px",
                  }}
                >
                  Est. Annuitized Jackpot Now
                  <br></br>
                  <span style={{ fontWeight: 800, fontSize: "1vw" }}>
                    {megaTitle}
                  </span>{" "}
                </div>
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  textAlign: "center",
                }}
                className="blink-slow"
              >
                <div
                  style={{
                    fontWeight: 800,
                    fontSize: "2vw",
                    maxHeight: 40,
                    padding: "8px 20px",
                    color: "black",
                  }}
                >
                  {storeMeta?.[0]?.store_meta_1}{" "}
                </div>
              </div>
            )}

            {!_.isEmpty(storeMeta?.[0]?.store_logo) ? (
              <div
                style={{
                  fontWeight: 800,
                  fontSize: "2vw",
                  maxHeight: 60,
                  color: "white",
                  textAlign: "center",
                  fontFamily: "Quantico",
                  flex: 2,
                }}
              >
                <Image
                  preview={false}
                  src={storeMeta?.[0]?.store_logo}
                  style={{
                    maxHeight: 70,
                  }}
                />
              </div>
            ) : (
              <div
                style={{
                  fontWeight: 800,
                  fontSize: "2vw",
                  maxHeight: 60,
                  color: "white",
                  textAlign: "center",
                  fontFamily: "Quantico",
                  flex: 2,
                }}
              >
                <p style={{ margin: 0, color: themeValues?.headerTitleColor }}>
                  Welcome To
                </p>
                <p
                  style={{
                    fontWeight: 800,
                    fontSize: "3vw",
                    margin: 0,
                    color: themeValues?.headerTitleColor,
                  }}
                >
                  {storeMeta?.[0]?.name}
                </p>
              </div>
            )}

            {_.isEmpty(storeMeta?.[0]?.store_meta_2) ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  textAlign: "center",
                  margin: "4px auto",
                }}
                className="blink-slow"
              >
                <div>
                  <Image
                    preview={false}
                    src={`https://lottery-data-v2.s3.us-east-2.amazonaws.com/original/96ee0ec5-5753-41fa-9948-3b2cf4913d59.png`}
                    style={{
                      maxHeight: 50,
                      maxWidth: 180,
                    }}
                  />
                </div>
                <div
                  style={{
                    fontWeight: 800,
                    fontSize: "0.5vw",
                    background: "red",
                    padding: "0.5vw",
                    color: "white",
                    borderRadius: "10px",
                    margin: "auto 10px",
                  }}
                >
                  Est. Annuitized Jackpot Now
                  <br></br>
                  <span style={{ fontWeight: 800, fontSize: "1vw" }}>
                    {powerTitle}
                  </span>{" "}
                </div>
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  textAlign: "center",
                }}
                className="blink-slow"
              >
                <div
                  style={{
                    fontWeight: 800,
                    fontSize: "2vw",
                    maxHeight: 40,
                    padding: "8px 20px",
                    color: "black",
                  }}
                >
                  {storeMeta?.[0]?.store_meta_2}{" "}
                </div>
              </div>
            )}
          </div>
        )}

        <div
          style={{
            flex: 1,
          }}
        >
          <SlotDisplay
            data={slotData || []}
            totalItemCount={totalItemCount}
            totalLargeCount={totalLargeCount}
            showAmontTicket={showAmontTicket}
            showTypeTicket={showTypeTicket}
            showScannedBox={showScannedBox}
            randomSlots={randomSlots}
            showMeta={showMeta}
            themeValues={themeValues}
          />
        </div>

        <div
          style={{
            height: "fit-content",
            display: "flex",
            flexDirection: "row",
            flexWrap: "wrap",
            justifyContent: "space-around",
            fontSize: "1vw",
          }}
        >
          <div
            onClick={() => {
              console.log("click sound test");
              speak({ text: "Sound Test" });
            }}
            style={{
              fontWeight: 600,
              color: "white",
              background: "red",
              padding: "0px 10px",
              borderRadius: "10px",
            }}
          >
            ATTENTION: Must be 18 years of age or older to play.
          </div>

          <div
            style={{
              fontWeight: 600,
              color: themeValues?.headerTitleColor,
              textAlign: "center",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
            }}
          >
            {/* Socket connection status indicator (click to view message log) */}
            <span
              title={`${
                isConnected ? "Connected" : "Disconnected"
              } — click to view messages`}
              onClick={() => setShowSocketLog(true)}
              style={{
                width: 12,
                height: 12,
                borderRadius: "50%",
                backgroundColor: isConnected ? "#22c55e" : "#ef4444",
                boxShadow: `0 0 6px ${isConnected ? "#22c55e" : "#ef4444"}`,
                flexShrink: 0,
                cursor: "pointer",
              }}
            />
            www.digitallotterysystem.com v3.20.0
          </div>
          <div
            onClick={() => {
              console.log("click sound cancel");
              cancel();
            }}
            style={{
              fontWeight: 600,
              color: "white",
              background: "red",
              padding: "0px 10px",
              borderRadius: "10px",
            }}
          >
            {storeConfig?.state_headline ??
              "ATTENTION: Lottery purchases are CASH only."}
          </div>
        </div>

        <BarcodeReader
          onError={(code: string) => handleScan(code, "by error")}
          onScan={(code: string) => handleScan(code, "by scan")}
        />
      </div>

      <SocketLogModal
        open={showSocketLog}
        onClose={() => setShowSocketLog(false)}
        isConnected={isConnected}
      />
    </div>
  );
}

const mapStateToProps = (state: RootState) => ({
  userId: state.user?.profileData?.data?.id,
  isAuth: state.user?.isAuth,
});

const mapDispatchToProps = (dispatch: any) => ({
  logout: () => dispatch(userActions.logout()),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(PageScreenV3);
